import { useCallback, useEffect, useRef, useState } from 'react';
import type { DragEvent } from 'react';

const hasFileDrag = (event: DragEvent): boolean => {
  return Array.from(event.dataTransfer?.types ?? []).includes('Files');
};

interface UseFileDropParams {
  enabled: boolean;
  disabled: boolean;
  onFiles: (files: File[]) => void;
}

const useFileDrop = ({ enabled, disabled, onFiles }: UseFileDropParams) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  const resetDragState = useCallback(() => {
    dragCounterRef.current = 0;
    setIsDragOver(false);
  }, []);

  const isDropActive = enabled && !disabled;

  // Clear overlay if the zone becomes inactive mid-drag (e.g. submit starts
  // streaming while the user is still holding files over the pane).
  useEffect(() => {
    if (!isDropActive) {
      resetDragState();
    }
  }, [isDropActive, resetDragState]);

  const handleDragEnter = useCallback(
    (event: DragEvent) => {
      if (!hasFileDrag(event)) {
        return;
      }

      // Always cancel the browser default for file drags so a drop while
      // disabled/loading does not navigate the tab away (HTML5 DnD requires
      // preventDefault on dragenter/dragover to make the element a drop target).
      event.preventDefault();

      if (!isDropActive) {
        return;
      }

      dragCounterRef.current += 1;
      setIsDragOver(true);
    },
    [isDropActive],
  );

  const handleDragOver = useCallback(
    (event: DragEvent) => {
      if (!hasFileDrag(event)) {
        return;
      }

      event.preventDefault();

      if (!isDropActive) {
        event.dataTransfer.dropEffect = 'none';
        return;
      }

      event.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);
    },
    [isDropActive],
  );

  const handleDragLeave = useCallback(
    (event: DragEvent) => {
      if (!hasFileDrag(event)) {
        return;
      }

      event.preventDefault();

      if (!isDropActive) {
        return;
      }

      dragCounterRef.current -= 1;

      if (dragCounterRef.current <= 0) {
        resetDragState();
      }
    },
    [isDropActive, resetDragState],
  );

  const handleDrop = useCallback(
    (event: DragEvent) => {
      if (!hasFileDrag(event)) {
        return;
      }

      event.preventDefault();
      resetDragState();

      if (!isDropActive) {
        return;
      }

      const dropped = Array.from(event.dataTransfer?.files ?? []);

      if (dropped.length === 0) {
        return;
      }

      onFiles(dropped);
    },
    [isDropActive, onFiles, resetDragState],
  );

  return {
    isDragOver,
    dropHandlers: {
      onDragEnter: handleDragEnter,
      onDragOver: handleDragOver,
      onDragLeave: handleDragLeave,
      onDrop: handleDrop,
    },
  };
};

export default useFileDrop;
